import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import { useMutation, gql, TypedDocumentNode, DocumentNode } from "@apollo/client";
import { NotePriorityValues } from "@/types";
import SuspensionForm from "./SuspensionForm";
import {
  SuspensionFormValues,
  getDefaultSuspensionFormValues,
  BASE_SUSPENSION_REASON_OPTIONS,
  RESTRICTED_SUSPENSION_REASON_OPTIONS
} from "./suspensionFormConstants";
import { useCanManageSuspensionsRestricted } from "@/hooks/useCapabilities";

const CREATE_SUSPENSION_MUTATION: TypedDocumentNode<
  {
    createSuspension: {
      success: boolean;
      message: string;
      suspension?: {
        id: string;
      };
    };
  },
  {
    input: {
      personId: string;
      code: string;
      startDate: string;
      endDate?: string | null;
      note?: {
        content: string;
        priority: string;
      } | null;
    };
  }
> = gql`
  mutation CreateSuspension($input: BackofficeCreateSuspensionInput!) {
    createSuspension(input: $input) {
      success
      message
      suspension {
        id
      }
    }
  }
`;

interface AddSuspensionModalProps {
  open: boolean;
  onClose: () => void;
  personId: string;
  pgaId: string;
  refetchQueries?: DocumentNode[];
  isPersonActive?: boolean;
  onSuspensionMutationSuccess: () => Promise<void> | void;
}

export default function AddSuspensionModal({
  open,
  onClose,
  personId,
  refetchQueries,
  onSuspensionMutationSuccess
}: AddSuspensionModalProps) {
  const [bannerError, setBannerError] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const canManageRestrictedSuspensions = useCanManageSuspensionsRestricted();

  // Filter suspension options based on user permissions
  // Restricted suspensions (e.g., Code of Ethics) require manageSuspensionsRestricted permission
  const availableSuspensionOptions = useMemo(() => {
    const options = [...BASE_SUSPENSION_REASON_OPTIONS];
    if (canManageRestrictedSuspensions) {
      options.push(...RESTRICTED_SUSPENSION_REASON_OPTIONS);
    }
    return options;
  }, [canManageRestrictedSuspensions]);

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    reset,
    formState: { isValid, isSubmitting }
  } = useForm<SuspensionFormValues>({
    defaultValues: getDefaultSuspensionFormValues(),
    mode: "onChange"
  });

  const [createSuspension, { loading: mutationLoading }] = useMutation(CREATE_SUSPENSION_MUTATION, {
    refetchQueries: refetchQueries || [],
    awaitRefetchQueries: true
  });

  const handleClose = () => {
    reset();
    setBannerError("");
    onClose();
  };

  async function submit(formData: SuspensionFormValues) {
    try {
      setBannerError("");

      const input = {
        personId,
        code: formData.suspensionReason,
        startDate: formData.startDate,
        endDate: formData.endDate || null,
        note: formData.note
          ? {
              content: formData.note,
              priority: formData.notePriority || NotePriorityValues.NONE
            }
          : null
      };

      const { data } = await createSuspension({ variables: { input } });

      if (data?.createSuspension?.success) {
        await onSuspensionMutationSuccess();
        enqueueSnackbar(data.createSuspension.message || "Suspension added successfully", { variant: "default" });
        handleClose();
      } else {
        setBannerError(data?.createSuspension?.message || "Failed to add suspension");
      }
    } catch (e) {
      const errorMessage = (e as Error).message || "An error occurred while adding suspension";
      setBannerError(`Error: ${errorMessage}`);
    }
  }

  return (
    <Dialog
      className="p-6 pr-2 max-h-[80vh] w-[500px]"
      open={open}
      onClosed={handleClose}
      headline="Add Suspension"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose} testId="add-suspension-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(submit)}
            testId="add-suspension-save-button"
            disabled={isSubmitting || !isValid || mutationLoading}
          >
            Save
          </Button>
        </>
      }
    >
      {!!bannerError && (
        <div className="mb-4">
          <Banner inline variant="error" description="The suspension could not be added." subText={bannerError} />
        </div>
      )}

      <SuspensionForm
        control={control}
        watch={watch}
        trigger={trigger}
        isEditMode={false}
        testIdPrefix="add-suspension"
        suspensionReasonOptions={availableSuspensionOptions}
      />
    </Dialog>
  );
}
